import type { HttpCore } from '../http';
import { ApiError } from '../errors';

/**
 * Coursebook chapter CONTENT (as opposed to the learner's personal reading
 * state — notes/highlights/progress — which lives in `modules/reading.ts`).
 * Types mirror `frontend/web-ide/src/services/reading.ts`.
 *
 * `getChapters`/`getChapter` are the two `/courses/:id/chapters*` paths
 * listed under "reading" in the mobile-app-plan §1 inventory; `signAudio`
 * and `getGlossary` are additional chapter-content endpoints the live reader
 * actually calls (`services/reading.ts`) that the compressed inventory table
 * didn't spell out.
 *
 * `stitchRecap` is the §1 inventory's "media" row (`/recaps/stitch`) — there
 * is no standalone `media` module in the requested module list, and a
 * lecture recap is course/lecture content, so it lives here. Mirrors
 * `frontend/web-ide/src/components/Recap/RecapPlayerContainer.tsx`.
 */

export interface Chapter {
  chapterId: string;
  chapterNumber: number;
  slug: string;
  title: string;
}

export interface ChapterContent {
  html: string;
  title: string;
  chapterNumber: number;
}

export interface AudioSign {
  url: string;
  expiresAt?: string;
}

export interface GlossaryTerm {
  term: string;
  definition: string;
}

/** Thrown when the audio-sign endpoint 404s (audio not generated yet) —
 *  distinguishes "hide the control" from a transient network error. */
export class AudioNotReadyError extends Error {
  constructor() {
    super('Audio not generated yet');
    this.name = 'AudioNotReadyError';
  }
}

export interface RecapStitchJob {
  job_id: string;
  estimated_time_ms: number;
  status: 'extracting' | 'stitching' | 'ready' | 'error';
}

function normalizeChapter(raw: Record<string, unknown>): Chapter {
  return {
    chapterId: (raw.chapterId as string) ?? (raw.id as string) ?? '',
    chapterNumber: (raw.chapterNumber as number) ?? (raw.chapter as number) ?? 0,
    slug: (raw.slug as string) ?? '',
    title: (raw.title as string) ?? '',
  };
}

export function createCoursesModule(core: HttpCore) {
  return {
    getChapters: async (courseId: string): Promise<Chapter[]> => {
      const data = await core.request<{ chapters?: unknown[] } | unknown[]>(
        `/courses/${encodeURIComponent(courseId)}/chapters`,
      );
      const rows = Array.isArray(data) ? data : (data?.chapters ?? []);
      return (rows as Record<string, unknown>[]).map(normalizeChapter).filter((c) => c.chapterId);
    },

    getChapter: async (courseId: string, chapterId: string): Promise<ChapterContent> => {
      const data = await core.request<Partial<ChapterContent>>(
        `/courses/${encodeURIComponent(courseId)}/chapters/${encodeURIComponent(chapterId)}`,
      );
      return { html: data?.html ?? '', title: data?.title ?? '', chapterNumber: data?.chapterNumber ?? 0 };
    },

    signAudio: async (courseId: string, chapterId: string): Promise<AudioSign> => {
      try {
        const data = await core.request<Partial<AudioSign>>(
          `/courses/${encodeURIComponent(courseId)}/chapters/${encodeURIComponent(chapterId)}/audio/sign`,
        );
        return { url: data?.url ?? '', expiresAt: data?.expiresAt };
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          throw new AudioNotReadyError();
        }
        throw err;
      }
    },

    getGlossary: async (courseId: string, chapterId: string): Promise<GlossaryTerm[]> => {
      try {
        const data = await core.request<{ terms?: unknown[] }>(
          `/courses/${encodeURIComponent(courseId)}/chapters/${encodeURIComponent(chapterId)}/glossary`,
        );
        const rows = Array.isArray(data?.terms) ? data.terms : [];
        return (rows as Record<string, unknown>[])
          .map((r) => ({
            term: typeof r?.term === 'string' ? r.term : '',
            definition: typeof r?.definition === 'string' ? r.definition : '',
          }))
          .filter((t) => t.term && t.definition);
      } catch {
        return [];
      }
    },

    stitchRecap: (lectureId: string, segmentIds: string[]) =>
      core.request<RecapStitchJob>('/recaps/stitch', {
        method: 'POST',
        body: { lecture_id: lectureId, segment_ids: segmentIds },
      }),
  };
}

export type CoursesModule = ReturnType<typeof createCoursesModule>;
